import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck2,
  ChevronDown,
  Clock3,
  Dumbbell,
  Flame,
  HeartPulse,
  Mic,
  ShieldCheck,
  Target,
} from 'lucide-react';

import { useLocale } from '../context/LocaleContext';
import { getAboutForzaContent } from '../utils/aboutForzaGuide';

const APP_STORE_URL = 'https://apps.apple.com/it/app/forza-fitness-longevit%C3%A0/id6758045302';

const AppleLogo = () => (
  <svg
    className="h-5 w-5 shrink-0"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    data-testid="apple-logo"
  >
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.79 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09ZM12.03 7.25C11.88 5.02 13.69 3.18 15.77 3c.29 2.58-2.34 4.5-3.74 4.25Z" />
  </svg>
);

const AppStoreLink = ({ children, testId }) => (
  <a
    className="about-button gap-2"
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
  >
    <AppleLogo />
    <span>{children}</span>
  </a>
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.74, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: 12,
    transition: { duration: 0.05, ease: "easeIn" },
  },
};

const stagger = {
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.04 },
  },
};

const HERO_PHONE_START_Y = '55vh';
const HERO_PHONE_MID_Y = '5vh';
const HERO_PHONE_EXIT_Y = '-5vh';
const HERO_SCROLL_MIDPOINT = 500;
const HERO_SCROLL_END = 1000;
const HERO_COPY_FADE_START = 24;
const HERO_COPY_FADE_END = 140;
const ABOUT_ICON_MAP = Object.freeze({
  calendar: CalendarCheck2,
  heart: HeartPulse,
  shield: ShieldCheck,
  target: Target,
  flame: Flame,
  mic: Mic,
  dumbbell: Dumbbell,
  clock: Clock3,
});

const AboutForzaPage = () => {
  const { locale } = useLocale();
  const content = useMemo(() => getAboutForzaContent(locale), [locale]);
  const [slideIndex, setSlideIndex] = useState(0);
  const touchStartXRef = useRef(null);
  const guideSlides = content.guideSlides;
  const activeSlide = guideSlides[slideIndex];
  const { scrollY } = useScroll();
  const heroCopyY = useTransform(scrollY, [0, HERO_SCROLL_MIDPOINT], ['0%', '-7%']);
  const heroCopyOpacity = useTransform(scrollY, [0, HERO_COPY_FADE_START, HERO_COPY_FADE_END], [1, 0.72, 0]);
  const heroPhoneY = useTransform(scrollY, [0, HERO_SCROLL_MIDPOINT, HERO_SCROLL_END], [HERO_PHONE_START_Y, HERO_PHONE_MID_Y, HERO_PHONE_EXIT_Y]);
  const heroPhoneScale = useTransform(scrollY, [0, HERO_SCROLL_MIDPOINT, HERO_SCROLL_END], [0.92, 1, 1.05]);
  const heroPhoneOpacity = useTransform(scrollY, [0, 1250, HERO_SCROLL_END], [1, 1, 0]);

  const goToSlide = useCallback((nextIndex) => {
    const total = guideSlides.length;
    setSlideIndex((nextIndex + total) % total);
  }, [guideSlides.length]);

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    touchStartXRef.current = null;

    if (typeof startX !== 'number' || typeof endX !== 'number') {
      return;
    }

    const distance = startX - endX;
    if (Math.abs(distance) < 44) {
      return;
    }

    goToSlide(slideIndex + (distance > 0 ? 1 : -1));
  };

  useEffect(() => {
    const preloadNeighbor = (offset) => {
      const totalSlides = guideSlides.length;
      const nextSlide = guideSlides[(slideIndex + offset + totalSlides) % totalSlides];
      if (!nextSlide?.imageSrc) return;
      const image = new Image();
      image.src = nextSlide.imageSrc;
    };

    preloadNeighbor(1);
    preloadNeighbor(-1);
  }, [guideSlides, slideIndex]);

  return (
    <>
      <style>{`
        :root {
          --about-bg: #121418;
          --about-bg-soft: #1a1d23;
          --about-text: #f8fafc;
          --about-body: #a8b0bd;
          --about-muted: #6f7b8f;
          --about-border: rgba(120, 133, 153, 0.22);
          --about-border-strong: rgba(34, 211, 238, 0.34);
          --about-surface: rgba(44, 47, 51, 0.86);
          --about-surface-soft: rgba(20, 24, 31, 0.92);
          --about-accent: #2dd4bf;
          --about-accent-strong: #06b6d4;
          --about-accent-alt: #2563eb;
          --about-button-text: #eff6ff;
        }

        .about-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at top center, rgba(37, 99, 235, 0.2), transparent 34%),
            radial-gradient(circle at 20% 18%, rgba(45, 212, 191, 0.12), transparent 24%),
            linear-gradient(180deg, #121418 0%, #171a20 42%, #111318 100%);
          color: var(--about-text);
          font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow-x: clip;
        }

        .about-shell {
          width: min(1120px, calc(100% - 2.8rem));
          margin: 0 auto;
        }

        .about-kicker {
          margin: 0;
          color: var(--about-muted);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .about-heading {
          margin: 0;
          color: var(--about-text);
          font-size: clamp(2rem, 4.5rem, 4rem);
          line-height: 0.98;
          font-weight: 700;
          letter-spacing: 0;
        }

        .about-body {
          margin: 0;
          color: var(--about-body);
          font-size: 1rem;
          line-height: 1.7;
          letter-spacing: 0;
        }

        .about-button {
          min-height: 3rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid transparent;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--about-accent-strong) 0%, var(--about-accent-alt) 100%);
          color: var(--about-button-text);
          box-shadow: 0 10px 28px rgba(37, 99, 235, 0.28);
          padding: 0.8rem 1.35rem;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          transition: transform 0.18s ease, background-color 0.18s ease;
        }

        .about-button:hover {
          background: linear-gradient(90deg, #22d3ee 0%, #3b82f6 100%);
          transform: translateY(-1px);
        }

        .about-button:focus-visible,
        .about-control:focus-visible,
        .about-dot:focus-visible,
        .about-input:focus-visible {
          outline: 2px solid rgba(34, 211, 238, 0.72);
          outline-offset: 2px;
        }

        .about-hero {
          position: relative;
          height: 215svh;
        }

        .about-hero-sticky {
          position: sticky;
          top: 0;
          height: 100svh;
          overflow: hidden;
        }

        .about-hero-content {
          position: absolute;
          inset-inline: 0;
          top: 0;
          z-index: 3;
          display: flex;
          justify-content: center;
          padding-top: max(6rem, 15svh);
          text-align: center;
        }

        .about-hero-copy-stack {
          width: min(42rem, calc(100% - 2rem));
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .about-hero-phone-stage {
          pointer-events: none;
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .about-hero-fade {
          pointer-events: none;
          position: absolute;
          inset-inline: 0;
          bottom: 0;
          z-index: 4;
          height: 15rem;
          background: linear-gradient(to bottom, transparent, var(--about-bg));
        }

        .about-hero-title {
          max-width: 13ch;
          font-size: clamp(2rem, 10vw, 4.25rem);
          text-align: center;
        }

        .about-hero-copy {
          max-width: 42ch;
          margin-top: 1.2rem;
          text-align: center;
        }

        .about-arrow {
          margin-top: 2.1rem;
          color: var(--about-muted);
          display: inline-flex;
          justify-content: center;
        }

        .about-phone-placeholder {
          position: relative;
          width: min(430px, 94vw);
          aspect-ratio: 1419 / 2796;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 2rem;
          background: #0d1015;
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
          padding: 0.8rem;
        }

        .about-phone-placeholder::before {
          content: '';
          position: absolute;
          top: 0.55rem;
          left: 50%;
          width: 34%;
          height: 0.32rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.22);
          transform: translateX(-50%);
        }

        .about-phone-screen {
          height: 100%;
          border-radius: 1.35rem;
          border: 1px solid rgba(34, 211, 238, 0.24);
          background: #0f141c;
          overflow: hidden;
          padding: 0;
          text-align: left;
        }

        .about-phone-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .about-screen-pill {
          width: 44%;
          height: 0.55rem;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--about-accent) 0%, var(--about-accent-strong) 100%);
        }

        .about-screen-card {
          margin-top: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.8rem;
          padding: 0.9rem;
          background: rgba(255, 255, 255, 0.035);
        }

        .about-section {
          padding: 5.8rem 0;
          border-top: 1px solid var(--about-border);
        }

        .about-section-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .about-section-header .about-heading,
        .about-section-header .about-body,
        .about-shell.text-center .about-heading,
        .about-shell.text-center .about-body {
          margin-left: auto;
          margin-right: auto;
        }

        .about-section-header .about-heading,
        .about-shell.text-center .about-heading {
          margin-top: 0.85rem;
        }

        .about-section-header .about-body,
        .about-shell.text-center .about-body {
          margin-top: 1.25rem;
        }

        .about-section-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 4rem;
          align-items: start;
        }

        .about-timeline {
          display: flex;
          flex-direction: column;
          gap: 1.35rem;
        }

        .about-why-timeline {
          width: min(720px, 100%);
          margin: 2.6rem auto 0;
        }

        .about-timeline-row {
          position: relative;
          padding-left: 1.35rem;
          border-left: 1px solid rgba(120, 133, 153, 0.24);
        }

        .about-timeline-row::before {
          content: '';
          position: absolute;
          left: -0.34rem;
          top: 0.48rem;
          width: 0.62rem;
          height: 0.62rem;
          border-radius: 999px;
          background: var(--about-accent);
          box-shadow: 0 0 0 6px rgba(34, 211, 238, 0.12);
        }

        .about-timeline-row strong {
          display: block;
          font-size: clamp(1.1rem, 3.8vw, 1.45rem);
          line-height: 1.35;
          color: var(--about-text);
          letter-spacing: 0;
        }

        .about-card-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
          margin-top: 2.4rem;
        }

        .about-card {
          min-height: 13.5rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border: 1px solid var(--about-border);
          border-radius: 1rem;
          background: transparent;
          padding: 1.35rem;
        }

        .about-card-icon {
          color: var(--about-accent);
          filter: drop-shadow(0 0 8px rgba(45, 212, 191, 0.2));
        }

        .about-card h3,
        .about-core-card h3 {
          margin: 1.1rem 0 0;
          font-size: 1.15rem;
          line-height: 1.25;
          font-weight: 700;
          letter-spacing: 0;
          color: var(--about-text);
        }

        .about-card p,
        .about-core-card p {
          margin: 0.9rem 0 0;
          color: var(--about-body);
          font-size: 0.88rem;
          line-height: 1.62;
        }

        .about-card-rule {
          width: 100%;
          height: 1px;
          margin-top: 1.1rem;
          background: linear-gradient(90deg, var(--about-accent) 0%, var(--about-accent-strong) 100%);
        }

        .about-walkthrough {
          width: min(580px, 100%);
          margin: 2.3rem auto 0;
        }

        .about-walkthrough-frame {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 0;
        }

        .about-slide-phone {
          width: min(500px, 100vw);
          max-height: 72svh;
          aspect-ratio: 1320 / 2868;
          object-fit: contain;
          filter: drop-shadow(0 20px 45px rgba(0, 0, 0, 0.42));
          user-select: none;
        }

        .about-control {
          position: absolute;
          top: 50%;
          z-index: 3;
          width: 2.55rem;
          height: 2.55rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(45, 212, 191, 0.28);
          border-radius: 999px;
          background: rgba(20, 24, 31, 0.88);
          color: var(--about-accent);
          transform: translateY(-50%);
        }

        .about-control-left {
          left: 0.65rem;
        }

        .about-control-right {
          right: 0.65rem;
        }

        .about-explanation {
          position: relative;
          z-index: 1;
          min-height: 9.25rem;
          margin: -4rem auto 0;
          width: min(390px, 92%);
          border: 1px solid rgba(34, 211, 238, 0.22);
          border-radius: 1.35rem;
          background: rgba(18, 22, 29, 0.96);
          box-shadow: 0 -18px 44px rgba(0, 0, 0, 0.35);
          padding: 1rem;
        }

        .about-explanation-top {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
        }

        .about-step-number {
          width: 1.8rem;
          height: 1.8rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 999px;
          background: rgba(45, 212, 191, 0.16);
          color: var(--about-accent);
          font-size: 0.72rem;
          font-weight: 800;
        }

        .about-explanation h3 {
          margin: 0;
          color: var(--about-text);
          font-size: 0.98rem;
          line-height: 1.35;
          font-weight: 800;
          letter-spacing: 0;
        }

        .about-explanation p {
          margin: 0.75rem 0 0;
          color: var(--about-body);
          font-size: 0.9rem;
          line-height: 1.58;
        }

        .about-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.4rem;
          margin-top: 1.15rem;
          flex-wrap: wrap;
        }

        .about-dot {
          width: 0.42rem;
          height: 0.42rem;
          border: 0;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.24);
          padding: 0;
          transition: width 0.18s ease, background-color 0.18s ease;
        }

        .about-dot-active {
          width: 1.6rem;
          background: var(--about-accent);
        }

        .about-core-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
          margin-top: 2.2rem;
        }

        .about-core-card {
          min-height: 11.5rem;
          border: 1px solid var(--about-border);
          border-radius: 1rem;
          background: var(--about-surface);
          padding: 1.25rem;
        }

        .about-core-card-active {
          border-color: rgba(0, 255, 136, 0.26);
          background: rgba(34, 211, 238, 0.06);
        }

        .about-core-icon {
          width: 2.3rem;
          height: 2.3rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.7rem;
          background: rgba(45, 212, 191, 0.12);
          color: var(--about-accent);
        }

        .about-form {
          width: min(720px, 100%);
          margin: 2rem auto 0;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.85rem;
        }

        .about-form-full {
          grid-column: 1 / -1;
        }

        .about-input {
          width: 100%;
          min-height: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 1.5rem;
          background: transparent;
          color: var(--about-text);
          padding: 0 1rem;
          font: inherit;
          font-size: 0.92rem;
        }

        textarea.about-input {
          min-height: 6.5rem;
          resize: vertical;
          border-radius: 1.1rem;
          padding-top: 0.9rem;
        }

        .about-input::placeholder {
          color: rgba(174, 183, 178, 0.62);
        }

        .about-error,
        .about-success {
          grid-column: 1 / -1;
          border-radius: 1rem;
          padding: 0.9rem 1rem;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .about-error {
          border: 1px solid rgba(251, 113, 133, 0.3);
          color: #fecdd3;
          background: rgba(244, 63, 94, 0.08);
        }

        .about-success {
          border: 1px solid rgba(52, 211, 153, 0.3);
          color: #bbf7d0;
          background: rgba(16, 185, 129, 0.08);
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            scroll-behavior: auto !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        @media (max-width: 900px) {
          .about-shell {
            width: calc(100% - 2.5rem);
          }

          .about-section-grid,
          .about-card-grid,
          .about-core-grid {
            grid-template-columns: 1fr;
          }

          .about-section-grid {
            gap: 2.5rem;
          }

          .about-heading {
            font-size: clamp(1.75rem, 8vw, 2.7rem);
          }

          .about-section {
            padding: 4.7rem 0;
          }
        }

        @media (max-width: 560px) {
          .about-shell {
            width: calc(100% - 2rem);
          }

          .about-hero {
            height: 190svh;
          }

          .about-hero-content {
            padding-top: max(5.25rem, 12svh);
          }

          .about-hero-title {
            font-size: clamp(2rem, 11vw, 2.75rem);
          }

          .about-phone-placeholder {
            width: min(340px, 90vw);
          }

          .about-walkthrough {
            width: min(420px, 100%);
            margin-top: 1.4rem;
          }

          .about-slide-phone {
            width: min(500px, 100vw);
            max-height: 68svh;
          }

          .about-explanation {
            min-height: 8.7rem;
            margin-top: -5rem;
            padding: 0.9rem;
          }

          .about-control-left {
            left: 0.45rem;
          }

          .about-control-right {
            right: 0.45rem;
          }

          .about-form {
            grid-template-columns: 1fr;
          }

          .about-button {
            width: 100%;
          }
        }
      `}</style>

      <main className="about-page" id="top">
        <section className="about-hero" id="hero">
          <div className="about-hero-sticky">
            <motion.div className="about-hero-content" style={{ y: heroCopyY, opacity: heroCopyOpacity }}>
              <div className="about-hero-copy-stack">
                <motion.h1
                  className="about-heading about-hero-title"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  exit="exit"
                >
                  {content.hero.title}
                </motion.h1>
                <motion.p
                  className="about-body about-hero-copy"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  transition={{ delay: 0.08 }}
                >
                  {content.hero.body}
                </motion.p>
                <motion.div
                  className="mt-7"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                >
                  <AppStoreLink testId="hero-app-store-link">
                    {content.hero.cta}
                  </AppStoreLink>
                </motion.div>
                <motion.div
                  className="about-arrow"
                  animate={{ y: [0, 7, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  aria-hidden="true"
                >
                  <ChevronDown size={24} />
                </motion.div>
              </div>
            </motion.div>

            <div className="about-hero-phone-stage">
              <motion.div
                className="about-phone-placeholder"
                style={{ y: heroPhoneY, scale: heroPhoneScale, opacity: heroPhoneOpacity }}
                aria-label={content.hero.phoneStageAria}
              >
                <div className="about-phone-screen">
                  <img
                    className="about-phone-image"
                    src="/about/homepage.jpg"
                    alt={content.hero.phoneImageAlt}
                    width="1320"
                    height="2868"
                    draggable="false"
                  />
                </div>
              </motion.div>
            </div>
            <div className="about-hero-fade" aria-hidden="true" />
          </div>
        </section>

        <section className="about-section" id="why">
          <div className="about-shell">
            <motion.div className="about-section-header" initial={false} animate="visible" variants={fadeUp}>
              <p className="about-kicker">{content.why.kicker}</p>
              <h2 className="about-heading max-w-[20ch]">{content.why.title}</h2>
            </motion.div>

            <motion.div
              className="about-timeline about-why-timeline"
              initial={false}
              animate="visible"
              variants={stagger}
            >
              {content.why.points.map((point) => (
                <motion.div className="about-timeline-row" key={point.id} variants={fadeUp}>
                  <strong>{point.lead}</strong>
                  <p className="about-body mt-1">{point.body}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="about-shell"
            initial={false}
            animate="visible"
            variants={stagger}
          >
            <div className="about-card-grid">
              {content.why.systems.map((system) => {
                const Icon = ABOUT_ICON_MAP[system.icon];
                return (
                  <motion.article className="about-card" key={system.id} variants={fadeUp}>
                    <div>
                      <Icon className="about-card-icon" size={46} aria-hidden="true" />
                      <h3>{system.title}</h3>
                      <div className="about-card-rule" />
                    </div>
                    <p>{system.detail}</p>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>
        </section>

        <section className="about-section" id="how-it-works">
          <div className="about-shell text-center">
            <motion.p className="about-kicker" initial={false} animate="visible" variants={fadeUp}>
              {content.walkthrough.kicker}
            </motion.p>
            <motion.h2 className="about-heading mx-auto mt-3 max-w-[16ch]" initial={false} animate="visible" variants={fadeUp}>
              {content.walkthrough.title}
            </motion.h2>

            <div className="about-walkthrough" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              <div className="about-walkthrough-frame">
                <button
                  type="button"
                  className="about-control about-control-left"
                  onClick={() => goToSlide(slideIndex - 1)}
                  aria-label={content.walkthrough.previousStep}
                >
                  <ArrowLeft size={18} />
                </button>
                <motion.img
                  key={activeSlide.id}
                  src={activeSlide.imageSrc}
                  alt={activeSlide.title}
                  className="about-slide-phone"
                  draggable="false"
                  initial={{ opacity: 0, x: 18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.22 }}
                />
                <button
                  type="button"
                  className="about-control about-control-right"
                  onClick={() => goToSlide(slideIndex + 1)}
                  aria-label={content.walkthrough.nextStep}
                >
                  <ArrowRight size={18} />
                </button>
              </div>

              <motion.article
                key={`${activeSlide.id}-copy`}
                className="about-explanation"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
              >
                <div className="about-explanation-top">
                  <span className="about-step-number">{String(slideIndex + 1).padStart(2, '0')}</span>
                  <h3>{activeSlide.title}</h3>
                </div>
                <p>{activeSlide.description}</p>
              </motion.article>

              <div className="about-dots" aria-label={content.walkthrough.stepsAria}>
                {guideSlides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={`about-dot ${index === slideIndex ? 'about-dot-active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={content.walkthrough.goToStep.replace('{step}', String(index + 1))}
                    aria-current={index === slideIndex ? 'true' : undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="about-section" id="features">
          <div className="about-shell">
            <motion.div className="about-section-header" initial={false} animate="visible" variants={fadeUp}>
              <p className="about-kicker">{content.features.kicker}</p>
              <h2 className="about-heading mt-3 max-w-[18ch]">{content.features.title}</h2>
              <p className="about-body mt-5 max-w-[54ch]">
                {content.features.body}
              </p>
            </motion.div>

            <motion.div
              className="about-core-grid"
              initial={false}
              animate="visible"
              variants={stagger}
            >
              {content.features.systems.map((system) => {
                const Icon = ABOUT_ICON_MAP[system.icon];
                return (
                  <motion.article
                    className={`about-core-card ${system.active ? 'about-core-card-active' : ''}`}
                    key={system.id}
                    variants={fadeUp}
                  >
                    <div className="about-core-icon">
                      <Icon size={20} aria-hidden="true" />
                    </div>
                    <h3>{system.title}</h3>
                    <p>{system.detail}</p>
                  </motion.article>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section className="about-section" id="download">
          <div className="about-shell text-center">
            <motion.h2 className="about-heading mx-auto max-w-[16ch]" initial={false} animate="visible" variants={fadeUp}>
              {content.waitlist.title}
            </motion.h2>
            <motion.p className="about-body mx-auto mt-5 max-w-[48ch]" initial={false} animate="visible" variants={fadeUp}>
              {content.waitlist.body}
            </motion.p>

            <div className="mt-8">
              <AppStoreLink testId="footer-app-store-link">
                {content.waitlist.submit}
              </AppStoreLink>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutForzaPage;
